import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeftIcon,
  BellIcon,
  BookOpenIcon,
  CalendarDaysIcon,
  ChevronRightIcon,
  EnvelopeIcon,
  MegaphoneIcon,
  SparklesIcon,
  TagIcon,
} from "@heroicons/react/24/outline";
import {
  loadEmailPreferences,
  readEmailPreferenceLinkParams,
  saveEmailPreferences,
  unsubscribeFromAll,
  type EmailPreferenceGroup,
  type EmailPreferenceState,
} from "../services/emailPreferences.service";

// Icons are matched from the catalog row (slug + name) rather than hard-coded per group
// id, so an admin adding or renaming a category still gets a sensible glyph. Ordered
// most-specific first: "Member newsletter" must reach the book, not the megaphone.
function groupIcon(group: EmailPreferenceGroup) {
  const value = `${group.slug} ${group.name}`.toLowerCase();
  if (value.includes("event")) {
    return CalendarDaysIcon;
  }
  if (value.includes("welcome")) {
    return SparklesIcon;
  }
  if (value.includes("news")) {
    return BookOpenIcon;
  }
  if (value.includes("promotion") || value.includes("offer")) {
    return TagIcon;
  }
  if (value.includes("marketing")) {
    return MegaphoneIcon;
  }
  if (value.includes("education") || value.includes("update")) {
    return BellIcon;
  }
  return TagIcon;
}

const EmailPreferences = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const manageMode = location.pathname.endsWith("/manage");
  const encodedParams = location.hash.startsWith("#")
    ? location.hash.slice(1)
    : location.search;
  const params = useMemo(
    () => readEmailPreferenceLinkParams(encodedParams),
    [encodedParams],
  );
  const [state, setState] = useState<EmailPreferenceState | null>(null);
  const [enabled, setEnabled] = useState<Set<number>>(new Set());
  const [loading, setLoading] = useState(Boolean(params));
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState<"saved" | "unsubscribed" | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // The signed URL contains an email address. Prevent it from becoming a
    // Referer when a visitor follows a footer link.
    let referrer = document.querySelector(
      'meta[name="referrer"]',
    ) as HTMLMetaElement | null;
    let robots = document.querySelector(
      'meta[name="robots"]',
    ) as HTMLMetaElement | null;
    const createdReferrer = !referrer;
    const createdRobots = !robots;
    const priorReferrer = referrer?.content;
    const priorRobots = robots?.content;
    if (!referrer) {
      referrer = document.createElement("meta");
      referrer.name = "referrer";
      document.head.appendChild(referrer);
    }
    if (!robots) {
      robots = document.createElement("meta");
      robots.name = "robots";
      document.head.appendChild(robots);
    }
    referrer.content = "no-referrer";
    robots.content = "noindex,nofollow";
    return () => {
      if (createdReferrer) {
        referrer?.remove();
      } else if (referrer) {
        referrer.content = priorReferrer ?? "";
      }
      if (createdRobots) {
        robots?.remove();
      } else if (robots) {
        robots.content = priorRobots ?? "";
      }
    };
  }, []);

  useEffect(() => {
    if (!params) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadEmailPreferences(params)
      .then((next) => {
        if (cancelled) {
          return;
        }
        setState(next);
        setEnabled(
          new Set(
            next.groups
              .filter((group) => group.enabled)
              .map((group) => group.sendgrid_group_id),
          ),
        );
      })
      .catch((reason: Error) => {
        if (!cancelled) {
          setError(reason.message);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [params]);

  const linkSuffix = location.hash || location.search;
  const friendlyError =
    !params || error === "invalid_or_expired_link"
      ? "This preference link is invalid or has expired. Please use the link from a more recent email."
      : error
        ? "We couldn't load or save your preferences right now. Please try again."
        : null;

  const unsubscribe = async () => {
    if (!params || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await unsubscribeFromAll(params);
      setDone("unsubscribed");
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    if (!params || saving) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveEmailPreferences(params, [...enabled]);
      setDone("saved");
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Resubscribe an already-unsubscribed recipient: opt back in to every visible
  // category (which globally re-opts-in per the backend rule that ≥1 enabled
  // category clears the voluntary unsubscribe), then reload so they land on the
  // normal checklist to fine-tune. A hard-bounce/spam suppression is preserved.
  const resubscribe = async () => {
    if (!params || saving || !state) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await saveEmailPreferences(
        params,
        state.groups.map((group) => group.sendgrid_group_id),
      );
      const next = await loadEmailPreferences(params);
      setState(next);
      setEnabled(
        new Set(
          next.groups
            .filter((group) => group.enabled)
            .map((group) => group.sendgrid_group_id),
        ),
      );
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbf8f1] text-[#20222b] flex flex-col">
      <main className="flex-1 px-5 pb-14">
        <div className="mx-auto w-full max-w-[460px] pt-9 sm:pt-12">
          <img
            src="/USLSlogonobackground.png"
            alt="U.S. LawShield"
            className="mx-auto h-24 w-auto object-contain"
          />

          {manageMode && (
            <button
              type="button"
              onClick={() =>
                navigate(`/email-preferences/unsubscribe${linkSuffix}`)
              }
              className="mt-10 mb-5 flex items-center gap-2 text-sm font-medium hover:underline"
            >
              <ArrowLeftIcon className="h-4 w-4" /> Back
            </button>
          )}

          <section
            className={`${manageMode ? "" : "mt-14"} rounded-xl border border-[#d2cec5] bg-white p-5 sm:p-6 shadow-sm`}
          >
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#20222b]">
              <EnvelopeIcon className="h-6 w-6" />
            </div>

            {done ? (
              <div className="py-7 text-center" role="status">
                <h1 className="font-serif text-3xl font-bold">
                  {done === "saved"
                    ? "Preferences saved"
                    : "You’re unsubscribed"}
                </h1>
                <p className="mt-3 text-sm leading-6 text-gray-600">
                  {done === "saved"
                    ? "Your email preferences have been updated."
                    : "You will no longer receive marketing emails from U.S. LawShield."}
                </p>
              </div>
            ) : manageMode ? (
              <>
                <h1 className="mt-5 text-center font-serif text-3xl font-bold">
                  Your email preferences
                </h1>
                <p className="mx-auto mt-2 max-w-sm text-center text-sm leading-5 text-gray-600">
                  Choose what you want to hear about from U.S. LawShield. You
                  can update your preferences anytime.
                </p>

                {loading ? (
                  <div className="py-16 text-center text-sm text-gray-500">
                    Loading your preferences…
                  </div>
                ) : friendlyError ? (
                  <div
                    role="alert"
                    className="mt-6 rounded-lg bg-red-50 p-4 text-sm text-red-800"
                  >
                    {friendlyError}
                  </div>
                ) : state && state.globally_unsubscribed ? (
                  <div className="mt-6 text-center" role="status">
                    <div className="rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
                      You’re currently unsubscribed from all U.S. LawShield
                      marketing emails.
                    </div>
                    {state.delivery_suppressed && (
                      <div className="mt-3 rounded-lg bg-amber-50 p-4 text-left text-xs leading-5 text-amber-900">
                        Note: this address also has a delivery issue on record
                        (a past bounce or spam report), so some emails may still
                        not be delivered even after you resubscribe.
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={resubscribe}
                      disabled={saving}
                      className="mt-5 w-full rounded-lg bg-[#b51d34] px-5 py-4 text-sm font-semibold text-white hover:bg-[#98182b] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving ? "Resubscribing…" : "Resubscribe to emails"}
                    </button>
                  </div>
                ) : state ? (
                  <>
                    {state.delivery_suppressed && (
                      <div className="mt-6 rounded-lg bg-amber-50 p-4 text-sm leading-5 text-amber-900">
                        This address has a delivery issue on record (a past
                        bounce or spam report). You can update your choices, but
                        some emails may still not be delivered.
                      </div>
                    )}
                    <div className="mt-6 overflow-hidden rounded-xl border border-[#d2cec5]">
                      {state.groups.map((group, index) => {
                        const Icon = groupIcon(group);
                        const checked = enabled.has(group.sendgrid_group_id);
                        return (
                          <label
                            key={group.id}
                            className={`flex cursor-pointer items-center gap-3 px-3 py-4 ${index ? "border-t border-[#d2cec5]" : ""}`}
                          >
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#d4d0c8] text-white">
                              <Icon className="h-5 w-5" />
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-sm font-semibold">
                                {group.name}
                              </span>
                              {group.description && (
                                <span className="mt-0.5 block text-xs leading-5 text-gray-600">
                                  {group.description}
                                </span>
                              )}
                            </span>
                            <input
                              type="checkbox"
                              className="peer sr-only"
                              checked={checked}
                              onChange={() =>
                                setEnabled((current) => {
                                  const next = new Set(current);
                                  if (checked) {
                                    next.delete(group.sendgrid_group_id);
                                  } else {
                                    next.add(group.sendgrid_group_id);
                                  }
                                  return next;
                                })
                              }
                            />
                            <span
                              aria-hidden="true"
                              className="relative h-6 w-10 shrink-0 rounded-full bg-[#c9c5bd] transition peer-checked:bg-[#b51d34] after:absolute after:left-1 after:top-1 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4"
                            />
                          </label>
                        );
                      })}
                    </div>
                    {state.groups.length === 0 && (
                      <div className="mt-6 rounded-lg bg-amber-50 p-4 text-sm text-amber-900">
                        No email categories are currently available.
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={save}
                      disabled={saving || state.groups.length === 0}
                      className="mt-5 w-full rounded-lg bg-[#b51d34] px-5 py-4 text-sm font-semibold text-white hover:bg-[#98182b] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {saving ? "Saving…" : "Save Preferences"}
                    </button>
                  </>
                ) : null}
              </>
            ) : (
              <>
                <h1 className="mt-5 text-center font-serif text-3xl font-bold">
                  Before you go
                </h1>
                {loading ? (
                  <div className="py-12 text-center text-sm text-gray-500">
                    Verifying your preference link…
                  </div>
                ) : friendlyError ? (
                  <div
                    role="alert"
                    className="mt-5 rounded-lg bg-red-50 p-4 text-sm text-red-800"
                  >
                    {friendlyError}
                  </div>
                ) : state ? (
                  <>
                    <p className="mt-2 text-center text-sm">
                      You’re about to unsubscribe
                    </p>
                    <p className="mt-1 break-all text-center text-sm font-semibold text-[#b51d34]">
                      {state.email}
                    </p>
                    <p className="mx-auto mt-6 max-w-sm text-center text-sm leading-6 text-gray-700">
                      By unsubscribing, you will stop receiving all marketing
                      emails from U.S. LawShield®. If you only want fewer
                      emails, you can adjust your preferences instead.
                    </p>
                    <button
                      type="button"
                      onClick={() =>
                        navigate(`/email-preferences/manage${linkSuffix}`)
                      }
                      className="mt-6 flex w-full items-center justify-center gap-2 rounded-lg bg-[#b51d34] px-5 py-4 text-sm font-semibold text-white hover:bg-[#98182b]"
                    >
                      Manage My Preferences{" "}
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </>
                ) : null}
              </>
            )}
          </section>

          {!done && state && (
            <button
              type="button"
              onClick={unsubscribe}
              disabled={saving}
              className="mx-auto mt-6 block text-sm font-medium underline disabled:opacity-50"
            >
              {saving ? "Updating…" : "Unsubscribe from all emails"}
            </button>
          )}
        </div>
      </main>

      <footer className="border-t border-[#d2cec5] px-6 py-7 text-xs text-gray-700">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <span>© 2026 U.S. LawShield. All Rights Reserved.</span>
          <nav className="flex gap-6">
            <a
              rel="noreferrer"
              href="https://www.uslawshield.com/privacy-policy/"
            >
              Privacy
            </a>
            <a
              rel="noreferrer"
              href="https://www.uslawshield.com/terms-of-use/"
            >
              Terms
            </a>
            <a rel="noreferrer" href="https://www.uslawshield.com/contact-us/">
              Contact
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default EmailPreferences;
